import Button from '@jarvis.oddle/core/components/Button'
import Flex from '@jarvis.oddle/core/components/Flex'
import Form, { useFormContext } from '@jarvis.oddle/core/components/Form'
import IconApplePay from '@jarvis.oddle/core/components/Icons/IconApplePay'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { P } from '@jarvis.oddle/core/components/Typo'
import {
  buttonShapes,
  buttonStyles,
  colorConstants,
  margin,
  marginSizes
} from '@jarvis.oddle/core/theme/default'
import { createCancellableCallback } from 'src/utils/function'
import { isObjectEmpty } from 'src/utils/object'
import { isLight } from '@jarvis.oddle/core/tools/style'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import React from 'react'
import styled, { ThemeContext } from 'styled-components'
import { paymentMethods, paymentTypes } from 'src/utils/payment'

const StyledApplePayIcon = styled(IconApplePay).attrs({
  fill: 'currentColor'
})`
  margin-left: ${margin[marginSizes.extraSmall]};
`

const StyledP = styled(P).attrs(() => ({
  normalised: true,
  bold: true,
  color: 'inherit'
}))`
  display: flex;
  align-items: center;
`

export const ApplePayButton = ({ ...props }): JSX.Element => {
  const { colorContainerColor } = React.useContext(ThemeContext)
  const { t } = React.useContext(TranslationContext)

  const isLightBackGround = isLight(colorContainerColor)
  const buttonColor = isLightBackGround
    ? colorConstants.black
    : colorConstants.white
  const buttonTextColor = isLightBackGround
    ? colorConstants.white
    : colorConstants.black

  const ApplePayIcon = <StyledApplePayIcon />

  return (
    <Button
      data-testid='button.applePay'
      buttonColor={buttonColor}
      buttonTextColor={buttonTextColor}
      buttonShape={buttonShapes.round}
      buttonStyle={buttonStyles.solid}
      {...props}
    >
      <StyledP>{t(`Buy with _ApplePayIcon_`, { ApplePayIcon })}</StyledP>
    </Button>
  )
}

const StyledApplePayButton = styled(ApplePayButton)`
  ${(props) =>
    props.mobileScaleFullWidth
      ? {
          width: '100%',
          margin: `${margin[marginSizes.none]} ${margin[marginSizes.medium]}`
        }
      : {
          width: props.width
        }}
  ${breakpoint('tablet')`
    ${(props) => ({ width: props.width })}
  `}
`

const StyledFormSubmitButton = styled(Form.SubmitButton)`
  ${(props) =>
    props.mobileScaleFullWidth
      ? {
          width: '100%',
          margin: `${margin[marginSizes.none]} ${margin[marginSizes.medium]}`
        }
      : {
          width: props.width
        }}
  ${breakpoint('tablet')`
    ${(props) => ({ width: props.width })}
  `}
`

interface PaymentMethods extends Oms.V4.PaymentMethodData {
  parsedId: string
}

interface SubmitButtonElementProps {
  selectedPaymentMethod: PaymentMethods
  buttonText: string
  stripePaymentRequestObject: {
    canMakePayment: () => Record<string, boolean>
    on: (x, y) => void
    show: () => void
  }
  isStripeReady: boolean
  mobileScaleFullWidth?: boolean
  isEnabledCustomerSaveCreditCardOnStripe?: boolean
  selectedSavedCreditCard?: Oms.V4.SelectedSavedCreditCard
}

const SubmitButtonElement = ({
  selectedPaymentMethod,
  buttonText,
  stripePaymentRequestObject,
  isStripeReady,
  mobileScaleFullWidth = false,
  isEnabledCustomerSaveCreditCardOnStripe = false,
  selectedSavedCreditCard
}: SubmitButtonElementProps): JSX.Element => {
  const form = useFormContext()

  const { type: paymentType, stripeRequestPaymentButtonType: buttonType } =
    selectedPaymentMethod || {}

  // disable the checkout button when user select stripe as the payment method and stripe
  // element has not been loaded into the iframe.
  let isDisabled = false
  if (paymentType === paymentTypes.stripe) {
    isDisabled = !isStripeReady
  } else if (paymentType === paymentTypes.stripeExpressAccount) {
    if (
      isEnabledCustomerSaveCreditCardOnStripe &&
      !isObjectEmpty(selectedSavedCreditCard) &&
      selectedPaymentMethod.paymentMethod !== paymentMethods.FPX &&
      selectedPaymentMethod.stripeRequestPaymentButtonType !==
        paymentMethods.APPLE_PAY
    ) {
      isDisabled = false
    } else {
      isDisabled = !isStripeReady
    }
  }

  const onStripeRequestButtonClick = React.useCallback(async () => {
    const { validateForm, setFieldValue, setFieldTouched, handleSubmit } = form
    // Validation
    const formErrors = await validateForm()
    if (!isObjectEmpty(formErrors)) return
    // Register on token callback
    const onTokenCallback = createCancellableCallback((paymentResponse) => {
      if (buttonType === paymentMethods.APPLE_PAY) {
        // Only have Apple Pay for now.
        setFieldValue(
          'stripeRequestPaymentButtonType',
          paymentMethods.APPLE_PAY,
          true
        )
        setFieldTouched('stripeRequestPaymentButtonType', true)
      }
      setFieldValue('stripeRequestPaymentToken', paymentResponse.token.id, true)
      setFieldTouched('stripeRequestPaymentToken', true)
      setFieldValue('stripeRequestPaymentResponse', paymentResponse)
      setFieldTouched('stripeRequestPaymentResponse', true)
      handleSubmit()
    })
    stripePaymentRequestObject.on('token', onTokenCallback)
    stripePaymentRequestObject.on('cancel', onTokenCallback.cancel)
    // Show the payment popup
    stripePaymentRequestObject.show()
  }, [buttonType, form, stripePaymentRequestObject])

  const ButtonElement = React.useMemo(() => {
    let ButtonElement

    if (
      (paymentType === paymentTypes.stripe ||
        paymentType === paymentTypes.stripeExpressAccount) &&
      buttonType === paymentMethods.APPLE_PAY
    ) {
      ButtonElement = (
        <StyledApplePayButton
          width='300px'
          disabled={isDisabled}
          onClick={onStripeRequestButtonClick}
          mobileScaleFullWidth={mobileScaleFullWidth}
        />
      )
    } else {
      ButtonElement = (
        <StyledFormSubmitButton
          width='300px'
          disabled={isDisabled}
          mobileScaleFullWidth={mobileScaleFullWidth}
        >
          {buttonText}
        </StyledFormSubmitButton>
      )
    }

    return ButtonElement
  }, [
    buttonText,
    buttonType,
    isDisabled,
    onStripeRequestButtonClick,
    paymentType,
    mobileScaleFullWidth
  ])

  return selectedPaymentMethod === null ? null : (
    <Flex flexDirection='row' justifyContent='center'>
      {ButtonElement}
    </Flex>
  )
}

export default SubmitButtonElement
